import { Vector2, Vector3 } from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { ProjectRenderer } from '../ProjectRenderer';
import { RenderUtils } from '../utils/RenderUtils';
import { DrawingVertex } from './DrawingVertex';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const COLORS = RENDERER_CONFIG.COLORS;

const { DRAWING_LINE } = RENDERER_CONFIG;

export const lineMaterial = new LineMaterial({
	color: COLORS.BLACK,
	linewidth: DRAWING_LINE.WIDTH,
});
const dashedLineMaterial = new LineMaterial({
	color: COLORS.BLACK,
	linewidth: DRAWING_LINE.WIDTH,
	dashed: true,
});

export const LINE_POS_Y = DRAWING_LINE.HEIGHT / 2;

export class DrawingLine {
	private line: Line2;
	private geometry = new LineGeometry();
	private material: LineMaterial;
	private _isError = false;
	start: DrawingVertex;
	end: DrawingVertex;
	position: Vector2;

	constructor(
		private renderer: ProjectRenderer,
		start: DrawingVertex,
		end: DrawingVertex
	) {
		this.start = start;
		this.end = end;

		this.position = new Vector2()
			.addVectors(start.position, end.position)
			.multiplyScalar(0.5);

		this.material = end.isMovable ? dashedLineMaterial.clone() : lineMaterial;
		this.line = new Line2(this.geometry, this.material);
		this.renderer.scene.add(this.line);

		this.compute();
	}

	get isError() {
		return this._isError;
	}

	set isError(value: boolean) {
		this._isError = value;
		this.end.isError = value;
		this.material.color.set(value ? COLORS.RED : COLORS.BLACK);
	}

	private doesIntersectWith(lineA: DrawingLine, lineB: DrawingLine) {
		const p1 = lineA.start.position;
		const p2 = lineA.end.position;
		const q1 = lineB.start.position;
		const q2 = lineB.end.position;

		const intersect =
			RenderUtils.ccw(p1, q1, q2) !== RenderUtils.ccw(p2, q1, q2) &&
			RenderUtils.ccw(p1, p2, q1) !== RenderUtils.ccw(p1, p2, q2);

		if (intersect) {
			const sharedPoints = [
				p1.equals(q1),
				p1.equals(q2),
				p2.equals(q1),
				p2.equals(q2),
			];
			if (sharedPoints.includes(true)) return false;
		}

		return intersect;
	}

	checkIntersectionsWithOthers(others: DrawingLine[]) {
		for (const other of others) {
			if (other === this) continue;
			if (this.doesIntersectWith(this, other)) {
				this.isError = true;
				return;
			}
		}
		this.isError = false;
	}

	compute() {
		this.geometry.setFromPoints([
			new Vector3(this.start.position.x, LINE_POS_Y, this.start.position.y),
			new Vector3(this.end.position.x, LINE_POS_Y, this.end.position.y),
		]);
		this.position = new Vector2()
			.addVectors(this.start.position, this.end.position)
			.multiplyScalar(0.5);

		this.line.computeLineDistances();
	}

	updateMaterial() {
		this.material = this.end.isMovable
			? dashedLineMaterial.clone()
			: lineMaterial;
		this.line.material = this.material;
	}

	dispose() {
		RenderUtils.disposeObject(this.line);
	}
}
